import React, { useState } from "react";
import { TableCell, TableRow, Typography, Stack, Avatar, Box } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";

import TableCommon from "../../Common/Table";
import { handleDateTimeDefault } from "../../../helpers/utils";
import ActionMenu, { ActionTypes } from "../../Common/ActionMenu";
import ConfirmationDialog from "../../Common/ConfirmationDialog";
import { baseUrl } from "../../../helpers/constants";

const WarrantyTableComponent = ({
    loading,
    fetching = false,
    count,
    data,
    onViewDetails,
    onDelete,
    pagination,
    handlePagination,
}) => {
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [warrantyToDelete, setWarrantyToDelete] = useState(null);

    const handleDeleteClick = (row) => {
        setWarrantyToDelete(row);
        setConfirmationOpen(true);
    };

    const handleConfirmDelete = () => {
        if (warrantyToDelete && onDelete) {
            onDelete(warrantyToDelete);
        }
        setConfirmationOpen(false);
        setWarrantyToDelete(null);
    };

    const handleCloseConfirmation = () => {
        setConfirmationOpen(false);
        setWarrantyToDelete(null);
    };

    let columns = [
        {
            id: "registration_number",
            title: "Registration #",
            field: "registration_number",
            minWidth: 140,
            sort: true,
        },
        {
            id: "customer",
            title: "Customer Details",
            minWidth: 200,
            sort: false,
        },
        {
            id: "product",
            title: "Product",
            minWidth: 150,
            sort: false,
        },
        {
            id: "purchase_info",
            title: "Purchase Info",
            minWidth: 180,
            sort: false,
        },
        {
            id: "created_at",
            title: "Registered Date",
            field: "created_at",
            minWidth: 120,
            sort: true,
        },
        {
            id: "actions",
            title: "Actions",
            minWidth: 100,
            sort: false,
            align: "center",
        },
    ];

    const renderTableData =
        data?.length > 0
            ? data.map((row, index) => {
                  return (
                      <TableRow hover key={row.id} tabIndex={-1}>
                          <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                  {row.registration_number}
                              </Typography>
                          </TableCell>
                          <TableCell>
                              <Stack spacing={0.5}>
                                  <Typography variant="body2" fontWeight="medium">
                                      {row.customer_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                      {row.mobile} • {row.email}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                      {row.city}, {row.state}
                                  </Typography>
                              </Stack>
                          </TableCell>
                          <TableCell>
                              <Stack spacing={0.5}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                      {row.product?.image_url && (
                                          <Avatar
                                              src={`${baseUrl}${row.product.image_url}`}
                                              alt={row.product.name}
                                              sx={{ width: 24, height: 24 }}
                                          />
                                      )}
                                      <Typography variant="body2" fontWeight="medium" noWrap>
                                          {row.product?.name || "N/A"}
                                      </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                      {row.product?.description || "No description"}
                                  </Typography>
                              </Stack>
                          </TableCell>
                          <TableCell>
                              <Stack spacing={0.5}>
                                  <Typography variant="body2" color="text.secondary">
                                      {row.purchase_date ? new Date(row.purchase_date).toLocaleDateString() : "N/A"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                      {row.dealer_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                      {row.dealer_state}
                                  </Typography>
                                  {(() => {
                                      let filesCount = 0;
                                      try {
                                          if (typeof row.files === "string") {
                                              const parsedFiles = JSON.parse(row.files);
                                              filesCount = Array.isArray(parsedFiles) ? parsedFiles.length : 0;
                                          } else if (Array.isArray(row.files)) {
                                              filesCount = row.files.length;
                                          }
                                      } catch (error) {
                                          filesCount = 0;
                                      }
                                      return (
                                          filesCount > 0 && (
                                              <Typography variant="caption" color="text.secondary">
                                                  {filesCount} file(s)
                                              </Typography>
                                          )
                                      );
                                  })()}
                              </Stack>
                          </TableCell>
                          <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                  {handleDateTimeDefault(row.created_at)}
                              </Typography>
                          </TableCell>
                          <TableCell align="center">
                              <ActionMenu
                                  actions={[
                                      {
                                          ...ActionTypes.VIEW,
                                          onClick: () => onViewDetails(row),
                                      },
                                      {
                                          ...ActionTypes.DELETE,
                                          onClick: () => handleDeleteClick(row),
                                      },
                                  ]}
                                  row={row}
                                  showStatusToggle={false}
                              />
                          </TableCell>
                      </TableRow>
                  );
              })
            : null;

    return (
        <>
            <TableCommon
                columns={columns}
                tableData={renderTableData}
                loading={loading}
                fetching={fetching}
                count={count}
                pagination={pagination}
                handlePagination={handlePagination}
            />
            <ConfirmationDialog
                open={confirmationOpen}
                title="Are you sure you want to delete this warranty registration?"
                confirmButtonTitle="Yes, Delete"
                onConfirm={handleConfirmDelete}
                onClose={handleCloseConfirmation}
            />
        </>
    );
};

export default WarrantyTableComponent;
